import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Checkbox,
  Chip,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Edit as EditIcon,
  PhotoLibrary as MediaIcon,
  Visibility as ViewIcon,
  WarningAmber as WarningIcon,
} from '@mui/icons-material';
import { VerifiedEntry } from '@/types/api';
import { EntryStatus } from '@/types/enums';
import { TelegramLinkButton } from '@/components/common/TelegramLinkButton';
import { formatDate } from '@/utils/dateUtils';

interface EntryGridCardProps {
  entry: VerifiedEntry;
  chatId: number;
  selected?: boolean;
  onToggleSelect?: (entryId: number) => void;
  onEdit: (entry: VerifiedEntry) => void;
  onViewContext?: (messageId: number) => void;
}

const getStatusColor = (status: EntryStatus): 'default' | 'success' | 'info' => {
  switch (status) {
    case EntryStatus.PUBLISHED:
      return 'success';
    case EntryStatus.REVIEWED:
      return 'info';
    default:
      return 'default';
  }
};

const EntryGridCard: React.FC<EntryGridCardProps> = React.memo(({
  entry,
  chatId,
  selected = false,
  onToggleSelect,
  onEdit,
  onViewContext,
}) => {
  const handleCardClick = (event: React.MouseEvent) => {
    const target = event.target as HTMLElement;
    if (target.closest('.hover-actions') || target.closest('button')) {
      return;
    }
    onEdit(entry);
  };

  const handleCheckboxClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.stopPropagation();
    onToggleSelect?.(entry.id);
  };

  const mediaCount = entry.media_links?.length || 0;

  return (
    <Card
      onClick={handleCardClick}
      sx={{
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        borderWidth: selected ? 2 : 1,
        borderStyle: 'solid',
        borderColor: selected
          ? 'primary.main'
          : entry.is_missing
            ? 'warning.main'
            : 'success.main',
        '&:hover': {
          boxShadow: 4,
          transform: 'translateY(-2px)',
        },
        '&:hover .hover-actions': {
          opacity: 1,
        },
      }}
    >
      <CardContent sx={{ p: 2, pb: '12px !important' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
            <Checkbox checked={selected} onChange={handleCheckboxClick} size="small" sx={{ p: 0.25 }} />
            <Chip label={entry.status} size="small" color={getStatusColor(entry.status)} sx={{ height: 20 }} />
            <Chip
              label={entry.ms_no ? `MS ${entry.ms_no}` : 'MS -'}
              size="small"
              variant="outlined"
              sx={{ height: 20 }}
            />
          </Box>

          <Tooltip title="Edit entry" arrow>
            <IconButton
              size="small"
              color="primary"
              onClick={(event) => {
                event.stopPropagation();
                onEdit(entry);
              }}
              sx={{ p: 0.5 }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 600,
            direction: 'rtl',
            textAlign: 'right',
            lineHeight: 1.4,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            mb: 0.75,
            minHeight: '2.8em',
          }}
        >
          {entry.title}
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ direction: 'rtl', textAlign: 'right', minHeight: '1.5em' }}
        >
          {entry.author || 'Unknown author'}
        </Typography>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          {entry.library?.canonical_name || 'Unknown library'}
        </Typography>

        <Box sx={{ display: 'flex', gap: 0.75, mt: 1.25, flexWrap: 'wrap' }}>
          {entry.is_missing && (
            <Chip
              icon={<WarningIcon fontSize="small" />}
              label="Missing"
              color="warning"
              size="small"
              sx={{ height: 20 }}
            />
          )}
          <Chip
            icon={<MediaIcon fontSize="small" />}
            label={`${mediaCount} media`}
            size="small"
            variant="outlined"
            sx={{ height: 20 }}
          />
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1.5 }}>
          <Typography variant="caption" color="text.secondary">
            Created {formatDate(entry.created_at, 'PP')}
          </Typography>

          <Box className="hover-actions" sx={{ display: 'flex', gap: 0.5, opacity: 0, transition: 'opacity 0.2s ease' }}>
            {onViewContext && (
              <Tooltip title={`View context #${entry.title_message_id}`} arrow>
                <IconButton
                  size="small"
                  onClick={(event) => {
                    event.stopPropagation();
                    onViewContext(entry.title_message_id);
                  }}
                >
                  <ViewIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <TelegramLinkButton chatId={chatId} messageId={entry.title_message_id} size="small" />
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
});

EntryGridCard.displayName = 'EntryGridCard';

export default EntryGridCard;
